import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Loader2, Download, Share2, Award, FileText, ExternalLink, BookOpen } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CertificadoSENDA() {
    const [status, setStatus] = useState(null);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [downloadingSocial, setDownloadingSocial] = useState(false);

    useEffect(() => {
        const init = async () => {
            try {
                const [statusRes, profileRes] = await Promise.all([
                    axios.get(`${API_URL}/api/user/certificate/status`, { withCredentials: true }),
                    axios.get(`${API_URL}/api/user/profile`, { withCredentials: true })
                ]);
                setStatus(statusRes.data);
                setProfile(profileRes.data);
            } catch {} finally { setLoading(false); }
        };
        init();
    }, []);

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const response = await axios.get(`${API_URL}/api/user/certificate/download`, {
                withCredentials: true,
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Certificado_SENDA_${status?.certificate_id || 'final'}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Certificado descargado');
            if (!status?.generated) {
                const { data } = await axios.get(`${API_URL}/api/user/certificate/status`, { withCredentials: true });
                setStatus(data);
            }
        } catch (error) { toast.error('Error al descargar el certificado'); }
        finally { setDownloading(false); }
    };

    const handleDownloadSocial = async () => {
        setDownloadingSocial(true);
        try {
            const response = await axios.get(`${API_URL}/api/user/certificate/social`, {
                withCredentials: true,
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Certificado_SENDA_Social_${status?.certificate_id || 'final'}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Versión para redes descargada');
        } catch (error) { toast.error('Error al descargar'); }
        finally { setDownloadingSocial(false); }
    };

    if (loading) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    if (!status?.eligible) return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col">
            <header className="bg-white/80 backdrop-blur-xl border-b border-[#EAE8E3]/50 sticky top-0 z-50">
                <div className="max-w-3xl mx-auto px-4 py-3">
                    <Link to="/usuario" className="flex items-center gap-2 text-[#3E5F6E] hover:text-[#2F4A57]"><ArrowLeft className="h-5 w-5" /><span className="text-sm font-medium">Volver</span></Link>
                </div>
            </header>
            <div className="flex-1 flex items-center justify-center p-4">
                <Card className="w-full max-w-md bg-white border border-[#DCE3E8] rounded-2xl"><CardContent className="pt-8 text-center">
                    <Award className="h-16 w-16 text-[#DCE3E8] mx-auto mb-4" />
                    <h2 className="text-xl font-light text-[#1F2A30] font-['Outfit'] mb-2">Certificado no disponible</h2>
                    <p className="text-sm text-[#616E7C] mb-6">Completa los 9 capítulos del programa SENDA para obtener tu certificado.</p>
                    <Link to="/usuario"><Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white">Continuar mi proceso</Button></Link>
                </CardContent></Card>
            </div>
            <SENDAFooter />
        </div>
    );

    const userName = profile?.name || 'Usuario SENDA';

    return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <header className="bg-white/80 backdrop-blur-xl border-b border-[#EAE8E3]/50 sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
                    <Link to="/usuario" className="flex items-center gap-2 text-[#3E5F6E] hover:text-[#2F4A57]"><ArrowLeft className="h-5 w-5" /><span className="text-sm font-medium">Volver al panel</span></Link>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/manual/download`} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="sm" className="border-[#5FAF9D]/30 text-[#5FAF9D] hover:bg-[#5FAF9D]/5" data-testid="manual-link"><BookOpen className="h-4 w-4 mr-2" />Manual</Button>
                    </a>
                </div>
            </header>

            <main className="flex-1 max-w-4xl mx-auto px-4 py-8 w-full">
                {/* Hero section */}
                <div className="text-center mb-10">
                    <div className="w-24 h-24 bg-gradient-to-br from-[#C4A35A] to-[#5FAF9D] rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl shadow-[#C4A35A]/20">
                        <Award className="h-12 w-12 text-white" />
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-light text-[#1F2A30] font-['Outfit'] mb-3" data-testid="certificate-page-title">Tu Certificado SENDA</h1>
                    <p className="text-[#616E7C] max-w-lg mx-auto">Has completado los 9 capítulos del programa. Este certificado reconoce tu compromiso con tu transformación personal.</p>
                </div>

                {/* Certificate Preview Card */}
                <Card className="bg-[#FDFCF9] border-2 border-[#C4A35A]/30 rounded-2xl shadow-xl mb-8 overflow-hidden" data-testid="certificate-preview">
                    <CardContent className="p-0">
                        <div className="relative">
                            {/* Decorative top border */}
                            <div className="h-1.5 bg-gradient-to-r from-[#C4A35A] via-[#5FAF9D] to-[#3E5F6E]" />

                            <div className="px-6 sm:px-12 py-8 sm:py-10 text-center">
                                {/* Logos */}
                                <div className="flex items-center justify-between mb-4 px-2">
                                    <img src="/humanos_reinvencion.jpeg" alt="Humanos en Reinvención" className="h-10 sm:h-12 object-contain" data-testid="cert-logo-hr" />
                                    <img src="/logo_senda.png" alt="SENDA" className="h-10 sm:h-12 object-contain" data-testid="cert-logo-senda" />
                                </div>

                                {/* Institution */}
                                <p className="text-sm text-[#3E5F6E] tracking-widest uppercase font-medium mb-1">Academia Humanos en Reinvención</p>
                                <div className="w-20 h-px bg-[#DCE3E8] mx-auto mb-4" />

                                <p className="text-xs text-[#7A8A93] mb-4 italic">Certifica que</p>

                                {/* Name - hero element */}
                                <h2 className="text-2xl sm:text-4xl font-bold text-[#1F2A30] tracking-wide mb-2 font-['Outfit']" data-testid="certificate-user-name">
                                    {userName.toUpperCase()}
                                </h2>
                                <div className="w-48 h-px bg-gradient-to-r from-transparent via-[#C4A35A] to-transparent mx-auto mb-6" />

                                {/* Main text */}
                                <p className="text-sm text-[#1F2A30] leading-relaxed max-w-xl mx-auto mb-4">
                                    ha completado satisfactoriamente el programa intensivo <strong>SENDA</strong>, un proceso de
                                    transformación personal orientado a la reducción de la procrastinación mediante el
                                    fortalecimiento de la identidad, la mentalidad y la gestión emocional.
                                </p>

                                <p className="text-xs text-[#7A8A93] leading-relaxed max-w-lg mx-auto mb-6">
                                    A lo largo de este recorrido, la persona participante desarrolló mayor conciencia
                                    sobre sus patrones de postergación, fortaleció su capacidad de acción y consolidó
                                    recursos personales para avanzar con más claridad, firmeza y dirección interna.
                                </p>

                                <p className="text-xs text-[#3E5F6E] italic leading-relaxed max-w-md mx-auto mb-6">
                                    Se extiende el presente certificado en reconocimiento a su compromiso con su
                                    crecimiento personal, su proceso de cambio y la construcción consciente de una
                                    versión más estable, decidida y alineada de sí misma.
                                </p>

                                {/* Signature */}
                                <div className="mb-6">
                                    <div className="w-36 h-px bg-[#3E5F6E] mx-auto mb-2" />
                                    <p className="text-sm font-semibold text-[#1F2A30]">Dra. Mariela Chiessa</p>
                                    <p className="text-xs text-[#7A8A93]">Psicóloga | Mentora | Creadora del método SENDA</p>
                                </div>

                                {/* Quote */}
                                <p className="text-xs text-[#5FAF9D] italic mb-6">"Un paso consciente puede cambiar una vida entera."</p>

                                {/* Footer info */}
                                <div className="border-t border-[#DCE3E8] pt-4 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 text-xs text-[#7A8A93]">
                                    <span>www.marielachiessa.com</span>
                                    <span className="hidden sm:inline text-[#C4A35A]">·</span>
                                    <span>appsenda.com</span>
                                    {status?.certificate_id && (
                                        <>
                                            <span className="hidden sm:inline text-[#C4A35A]">·</span>
                                            <span className="font-mono text-[#9AA5B1]" data-testid="certificate-id">ID: {status.certificate_id}</span>
                                        </>
                                    )}
                                </div>
                                <p className="text-[10px] text-[#9AA5B1] mt-2">Powered by Dataismo AI Technologies</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
                    <Button
                        onClick={handleDownload}
                        disabled={downloading}
                        className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl shadow-lg"
                        data-testid="download-certificate-btn"
                    >
                        {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
                        Descargar certificado
                    </Button>

                    <Button
                        onClick={handleDownloadSocial}
                        disabled={downloadingSocial}
                        variant="outline"
                        className="border-[#5FAF9D] text-[#5FAF9D] hover:bg-[#5FAF9D]/5 px-8 py-3 rounded-xl"
                        data-testid="download-social-btn"
                    >
                        {downloadingSocial ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Share2 className="h-4 w-4 mr-2" />}
                        Versión para redes
                    </Button>
                </div>

                {/* Info cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Card className="bg-white border border-[#DCE3E8] rounded-xl">
                        <CardContent className="pt-5">
                            <div className="flex items-start gap-3">
                                <div className="w-9 h-9 bg-[#3E5F6E]/10 rounded-lg flex items-center justify-center shrink-0">
                                    <FileText className="h-4 w-4 text-[#3E5F6E]" />
                                </div>
                                <div>
                                    <h4 className="font-semibold text-sm text-[#1F2A30] font-['Outfit'] mb-1">Formato PDF</h4>
                                    <p className="text-xs text-[#616E7C]">El certificado se descarga en formato PDF de alta calidad, listo para imprimir o archivar digitalmente.</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-white border border-[#DCE3E8] rounded-xl">
                        <CardContent className="pt-5">
                            <div className="flex items-start gap-3">
                                <div className="w-9 h-9 bg-[#5FAF9D]/10 rounded-lg flex items-center justify-center shrink-0">
                                    <ExternalLink className="h-4 w-4 text-[#5FAF9D]" />
                                </div>
                                <div>
                                    <h4 className="font-semibold text-sm text-[#1F2A30] font-['Outfit'] mb-1">Versión para redes</h4>
                                    <p className="text-xs text-[#616E7C]">La versión cuadrada está optimizada para compartir en redes sociales como Instagram o LinkedIn.</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </main>

            <SENDAFooter />
        </div>
    );
}
